import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { fetcherWithToken } from '@/shared/api/fetcher';
import {
  useIsUserLoading,
  useIsAuth,
  useUserId,
} from '@/shared/providers/user-provider/use-user';
import { useNotificationsSelector } from '@/shared/providers/notifications-provider/store';
import type { HomeGamesResponse } from '@/widgets/game-sections/ui/home-games-types';

export function useHomeGames(initialHomeGames?: HomeGamesResponse | null) {
  const isUserLoading = useIsUserLoading();
  const isAuth = useIsAuth();
  const userId = useUserId();
  const isBonusActive = useNotificationsSelector((s) =>
    s.notifications?.bonus === undefined
      ? undefined
      : !!s.notifications.bonus?.isActive
  );
  const revalidatedBonusStateRef = useRef<boolean>();

  const homeGamesKey = isUserLoading
    ? null
    : (['/homeGames', isAuth, userId ?? null] as const);

  const { data, error, mutate } = useSWR<HomeGamesResponse>(
    homeGamesKey,
    async ([url]) => fetcherWithToken(url),
    {
      fallbackData: isAuth ? undefined : (initialHomeGames ?? undefined),
      revalidateIfStale: false,
    }
  );

  useEffect(() => {
    if (!data || isBonusActive === undefined) return;
    if (
      !!data.bonus?.length === isBonusActive ||
      revalidatedBonusStateRef.current === isBonusActive
    )
      return;

    revalidatedBonusStateRef.current = isBonusActive;
    mutate();
  }, [data, isBonusActive, mutate]);

  return { data, error, isAuth };
}
